import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { t } from "@/lib/lang";
import {
  getMyAttendanceStatus,
  startBreak,
  endBreak,
  dutyOut,
  dutyIn,
  resumeDuty,
  pauseForGap,
  postponeGap,
} from "@/lib/attendanceApi";

/**
 * Staff attendance state.
 *
 * CRITICAL (bible §66.3 Section 8):
 * - No localStorage, No IndexedDB, No service-worker cache. React state only.
 * - Single source of truth is the backend database.
 *
 * One AttendanceProvider per screen (CaptainLayout, AppShell) holds it, so the pill,
 * the split-break prompt and the START DUTY bar always show the same answer.
 * A widget outside any provider keeps its own.
 */
const AttendanceContext = createContext(null);

function useAttendanceState(token) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  // A read that failed is shown, never hidden (operating law 3).
  const [failed, setFailed] = useState(false);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const data = await getMyAttendanceStatus(token);
      if (mountedRef.current) {
        setStatus(data);
        setFailed(false);
      }
    } catch (err) {
      if (mountedRef.current) setFailed(true);
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    mountedRef.current = true;
    fetchStatus();
    // Every 30 s: elapsed time, and the split-break prompt and START DUTY bar the
    // server decides on.
    const interval = setInterval(() => fetchStatus(true), 30000);
    const onFocus = () => fetchStatus(true);
    window.addEventListener("focus", onFocus);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchStatus, token]);

  // Every action: the server's answer becomes the state; the toast follows it.
  const act = async (call, okText, failText) => {
    if (!token || acting) return null;
    setActing(true);
    try {
      const data = await call();
      setStatus(data);
      setFailed(false);
      if (okText) toast.success(t(okText), { duration: 3000 });
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : failText);
      fetchStatus(true);
      return null;
    } finally {
      setActing(false);
    }
  };

  return {
    status,
    failed,
    loading,
    acting,
    refresh: fetchStatus,
    dutyIn: () => act(() => dutyIn(token), "ड्यूटी शुरू · Duty started", "Could not start duty."),
    startBreak: (notes = null) => act(() => startBreak(token, notes), "ब्रेक शुरू हुआ · Break started", "Could not start break."),
    endBreak: () => act(() => endBreak(token), "ड्यूटी पर वापस · Resumed duty", "Could not end break."),
    dutyOut: (notes = null) => act(() => dutyOut(token, notes), "ड्यूटी समाप्त · Duty ended", "Could not complete duty out."),
    resume: () => act(() => resumeDuty(token), "ड्यूटी शुरू · Duty started", "Could not start duty."),
    pauseGap: () => act(() => pauseForGap(token), "ड्यूटी रुकी · Duty paused for the split break", "Could not pause duty."),
    postponeGap: () => act(() => postponeGap(token), "15 मिनट बाद पूछेंगे · We'll ask again in 15 min", "Could not postpone."),
  };
}

export function AttendanceProvider({ token, children }) {
  const value = useAttendanceState(token);
  return createElement(AttendanceContext.Provider, { value }, children);
}

export function useAttendance(token) {
  const shared = useContext(AttendanceContext);
  const own = useAttendanceState(shared ? null : token);
  return shared || own;
}
